import { hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { createResource, listResource, type MaterialRow, type StockRow } from "@/lib/master-data";

export function ConsumablesPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [inboundOpen, setInboundOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inbound, setInbound] = useState({
    materialId: "",
    batchDate: new Date().toISOString().slice(0, 10),
    quantity: "10",
    price: "",
    supplier: "",
    expiryDate: "",
  });
  const [issue, setIssue] = useState({ materialId: "", quantity: "1", purpose: "" });

  const canManage = Boolean(me && hasPermission(me.permissions, "inventory:manage"));
  const stockQuery = useQuery({
    queryKey: ["consumable-stock"],
    queryFn: () => listResource<StockRow>("/api/v1/consumables/stock"),
  });
  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });
  const stock = stockQuery.data ?? [];
  const materials = materialsQuery.data ?? [];
  const consumableMaterials = materials.filter(
    (item) => item.category === "consumable" && item.status === "active",
  );
  const totalQuantity = stock.reduce((sum, row) => sum + row.quantity, 0);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["consumable-stock"] });
  }

  async function submitInbound() {
    if (!inbound.materialId) return toast.error("请选择耗材物料");
    if (!inbound.price.trim()) return toast.error("请填写单价");
    setBusy(true);
    try {
      await createResource("/api/v1/consumables/inbound", {
        materialId: inbound.materialId,
        batchDate: inbound.batchDate,
        quantity: Number(inbound.quantity),
        purchaseUnitPriceYuan: Number(inbound.price),
        supplier: inbound.supplier || null,
        expiryDate: inbound.expiryDate || null,
      });
      toast.success("耗材批次已入库");
      setInboundOpen(false);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "入库失败");
    } finally {
      setBusy(false);
    }
  }

  async function submitIssue() {
    if (!issue.materialId) return toast.error("请选择耗材");
    setBusy(true);
    try {
      await createResource("/api/v1/consumables/issue", {
        materialId: issue.materialId,
        quantity: Number(issue.quantity),
        purpose: issue.purpose || null,
      });
      toast.success("耗材已领用");
      setIssueOpen(false);
      invalidate();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "领用失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="耗材库存"
        description="按物料汇总库存；入库建批次，领用扣减结存，库存不足会被服务端和数据库双重拦截。"
        actions={
          <>
            <Button variant="outline" onClick={() => stockQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            {canManage ? (
              <>
                <Button variant="outline" onClick={() => setIssueOpen(true)}>
                  领用
                </Button>
                <Button onClick={() => setInboundOpen(true)}>
                  <Plus className="h-4 w-4" />
                  批次入库
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard label="库存总量" value={totalQuantity} icon={Package} />
        <StatCard label="耗材种类" value={stock.length} icon={Package} accent="success" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>物料结存</CardTitle>
          <CardDescription>结存为 0 时不会删除记录，便于补货与审计。</CardDescription>
        </CardHeader>
        <CardContent>
          {stockQuery.isLoading ? (
            <div className="space-y-2">
              {["c1", "c2", "c3"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : stock.length === 0 ? (
            <EmptyState title="暂无耗材库存" description="先进行批次入库。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>物料编码</TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>库存</TableHead>
                  <TableHead>单位</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stock.map((row) => (
                  <TableRow key={row.materialId}>
                    <TableCell className="font-mono text-xs">{row.materialCode}</TableCell>
                    <TableCell className="font-medium">{row.materialName}</TableCell>
                    <TableCell>{row.quantity}</TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell className="text-right">
                      {canManage ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setIssue({ materialId: row.materialId, quantity: "1", purpose: "" });
                            setIssueOpen(true);
                          }}
                        >
                          领用
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={inboundOpen} onOpenChange={setInboundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>耗材批次入库</DialogTitle>
            <DialogDescription>每次入库生成独立批次与库存流水。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>物料</Label>
              <select
                value={inbound.materialId}
                onChange={(event) => setInbound({ ...inbound, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择耗材物料</option>
                {consumableMaterials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.code} · {material.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>批次日期</Label>
              <Input
                type="date"
                value={inbound.batchDate}
                onChange={(event) => setInbound({ ...inbound, batchDate: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>数量</Label>
              <Input
                type="number"
                min="1"
                value={inbound.quantity}
                onChange={(event) => setInbound({ ...inbound, quantity: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>单价（元）</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={inbound.price}
                onChange={(event) => setInbound({ ...inbound, price: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>供应商</Label>
              <Input
                value={inbound.supplier}
                onChange={(event) => setInbound({ ...inbound, supplier: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>有效期</Label>
              <Input
                type="date"
                value={inbound.expiryDate}
                onChange={(event) => setInbound({ ...inbound, expiryDate: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInboundOpen(false)}>
              取消
            </Button>
            <Button onClick={submitInbound} disabled={busy}>
              确认入库
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>耗材领用</DialogTitle>
            <DialogDescription>库存不足时整笔拒绝，不会产生负数库存。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>耗材</Label>
              <select
                value={issue.materialId}
                onChange={(event) => setIssue({ ...issue, materialId: event.target.value })}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择</option>
                {stock.map((row) => (
                  <option key={row.materialId} value={row.materialId}>
                    {row.materialCode} · {row.materialName}（库存 {row.quantity}）
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>数量</Label>
              <Input
                type="number"
                min="1"
                value={issue.quantity}
                onChange={(event) => setIssue({ ...issue, quantity: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>用途/领用部门</Label>
              <Input
                value={issue.purpose}
                onChange={(event) => setIssue({ ...issue, purpose: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueOpen(false)}>
              取消
            </Button>
            <Button onClick={submitIssue} disabled={busy}>
              确认领用
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
